'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';

export default function LoginPage() {
  const router = useRouter();
  const [step, setStep] = useState<'login' | 'otp'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const API = process.env.NEXT_PUBLIC_API_URL;

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Login failed');
      setStep('otp');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleOTP(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: otp }),
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Invalid code');
      router.push('/dashboard');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="min-h-screen bg-base flex items-center justify-center">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold text-text-strong tracking-tight">QUEST</h1>
          <p className="text-text-muted mt-2 text-sm">Acknowledged Route Discovery Engine</p>
        </div>

        <div className="bg-surface border border-border rounded-2xl p-8">
          {step === 'login' ? (
            <form onSubmit={handleLogin}>
              <h2 className="text-text-strong text-xl font-semibold mb-6">Sign in</h2>
              <div className="space-y-4">
                <div>
                  <label className="text-text-muted text-sm mb-1 block">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="w-full bg-subtle border border-border rounded-lg px-4 py-3 text-text placeholder:text-text-faint focus:outline-none focus:border-accent transition"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <label className="text-text-muted text-sm mb-1 block">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="w-full bg-subtle border border-border rounded-lg px-4 py-3 text-text placeholder:text-text-faint focus:outline-none focus:border-accent transition"
                    placeholder="••••••••"
                  />
                </div>
                {error && <p className="text-danger-fg text-sm">{error}</p>}
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-accent hover:bg-accent-hover disabled:opacity-50 text-white font-semibold py-3 rounded-lg transition mt-4"
              >
                {loading ? 'Sending code...' : 'Continue'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleOTP}>
              <h2 className="text-text-strong text-xl font-semibold mb-2">Check your email</h2>
              <p className="text-text-muted text-sm mb-6">
                We sent a 6-digit code to <span className="text-text-strong">{email}</span>
              </p>
              <div className="space-y-4">
                <div>
                  <label className="text-text-muted text-sm mb-1 block">Access code</label>
                  <input
                    type="text"
                    value={otp}
                    onChange={e => setOtp(e.target.value)}
                    maxLength={6}
                    className="w-full bg-subtle border border-border rounded-lg px-4 py-3 text-text text-center text-2xl tracking-widest placeholder:text-text-faint focus:outline-none focus:border-accent transition"
                    placeholder="000000"
                  />
                </div>
                {error && <p className="text-danger-fg text-sm">{error}</p>}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-accent hover:bg-accent-hover disabled:opacity-50 text-white font-semibold py-3 rounded-lg transition"
                >
                  {loading ? 'Verifying...' : 'Sign in'}
                </button>
                <button
                  type="button"
                  onClick={() => { setStep('login'); setError(''); setOtp(''); }}
                  className="w-full text-text-faint hover:text-text-muted text-sm transition"
                >
                  Back
                </button>
              </div>
            </form>
          )}
        </div>
        <p className="text-text-faint text-xs text-center mt-6">
          Confidential — Authorised access only
        </p>
      </div>
    </main>
  );
}
